/** 261104 E1-P1 — mảnh dùng chung của «Chuyển thể mới»: thanh 4 cổng, chữ có mốc, thẻ nhãn, thanh quyết định, ô «sửa bằng lời». Không import antd. */
import React, { useEffect, useRef } from 'react';
import type { SkillGate, SkillRunProjection } from '../../../types/skillAdaptation';
import { SKILL_GATES } from '../../../types/skillAdaptation';
import { GATE_TITLE, instructionHeightOf, linkMilestones, stepStatesOf } from './skillAdaptUtils';

export const TAG: Record<string, [string, string]> = { src: ['keep', 'Nguồn'], SOURCE: ['keep', 'Nguồn'], keep: ['keep', 'Giữ'], MERGED: ['merge', 'Gộp'], merge: ['merge', 'Gộp'], NEW: ['new', 'Mới'], new: ['new', 'Mới'] };
export const Tag: React.FC<{ kind: string }> = ({ kind }) => { const [cls, label] = TAG[kind] ?? ['', kind]; return <span className={`tag ${cls}`}>{label}</span>; };

/** Chữ có mốc B01…: hiện tên ngắn + mã mốc (nhỏ). Mốc chưa biết tên thì chỉ hiện mã. */
export const Ms: React.FC<{ text?: string | null; names: Record<string, string> }> = ({ text, names }) => (
  <>{linkMilestones(text ?? '', names).map((s, i) => {
    if (s.kind === 'text') return <React.Fragment key={i}>{s.text}</React.Fragment>;
    const code = s.ids.map((x) => x.replace(/B(\d)/, 'B​$1')).join('–');
    return <span className="mref" key={i}>{s.name ? <span>{s.name}</span> : null}<span className="mcode" title={`Mốc ${s.ids.join('–')} trong khung (mã nội bộ)`}>{code}</span></span>;
  })}</>
);

export const StepBar: React.FC<{ run: SkillRunProjection | null; viewGate: SkillGate | null; onView: (g: SkillGate | null) => void }> = ({ run, viewGate, onView }) => {
  const states = run ? stepStatesOf(run) : (['locked', 'locked', 'locked', 'locked'] as const);
  const note = (st: string, g: SkillGate): string => (st === 'done' ? 'Đã chốt' : st === 'active' ? 'Chờ bạn' : run && run.status === 'RUNNING' && run.approvedGates.length === SKILL_GATES.indexOf(g) ? 'Máy đang chuẩn bị' : 'Sau quyết định trước');
  return (
    <nav className="steps" aria-label="Bốn quyết định của bạn">
      {SKILL_GATES.map((g, i) => {
        const st = states[i]; const clickable = st !== 'locked';
        return (
          <button key={g} type="button" className={`step ${st === 'locked' ? 'locked' : st} ${viewGate === g ? 'viewing' : ''}`} disabled={!clickable} onClick={() => onView(st === 'active' ? null : g)} aria-current={st === 'active' ? 'step' : undefined}>
            <span className="num">{st === 'done' ? '✓' : i + 1}</span><span><b>{GATE_TITLE[g]}</b><small>{note(st, g)}</small></span>
          </button>
        );
      })}
    </nav>
  );
};

/** Thanh quyết định dính đáy (sticky). */
export const DecisionBar: React.FC<{ title: string; sub?: string; label: string; disabled: boolean; onClick: () => void; extra?: React.ReactNode }> = ({ title, sub, label, disabled, onClick, extra }) => (
  <footer className="row between action">
    <div className="grow"><b>{title}</b>{sub ? <p className="muted">{sub}</p> : null}</div>
    {extra}
    <button type="button" className="btn primary" disabled={disabled} onClick={onClick}>{label}</button>
  </footer>
);

/** Ô «sửa bằng lời» — tự giãn theo nội dung, tối đa 160px. */
export const InstructionBox = React.forwardRef<HTMLTextAreaElement, { value: string; onChange: (v: string) => void; onSubmit: () => void; disabled: boolean; submitDisabled: boolean; busyLabel?: string }>(
  ({ value, onChange, onSubmit, disabled, submitDisabled, busyLabel }, ref) => {
    const inner = useRef<HTMLTextAreaElement | null>(null);
    const setRef = (el: HTMLTextAreaElement | null): void => { inner.current = el; if (typeof ref === 'function') ref(el); else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el; };
    useEffect(() => { const el = inner.current; if (!el) return; el.style.height = 'auto'; el.style.height = `${instructionHeightOf(el.scrollHeight)}px`; }, [value]);
    return (
      <div className="instr">
        <textarea ref={setRef} rows={1} aria-label="Sửa bằng lời" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} placeholder="Sửa bằng lời — ví dụ: giữ thêm hậu quả của việc nhân vật chính trao bản sao cho người kia" />
        <button type="button" className="btn primary" disabled={disabled || submitDisabled || !value.trim()} onClick={onSubmit}>{busyLabel ?? 'Nhờ máy sửa'}</button>
      </div>
    );
  },
);
InstructionBox.displayName = 'InstructionBox';

export const Alert: React.FC<{ kind?: 'bad' | 'ok' | 'blue' | ''; children: React.ReactNode }> = ({ kind = '', children }) => <div className={`alert ${kind}`} role={kind === 'bad' ? 'alert' : 'status'}>{children}</div>;
